import { AnimatePresence, motion } from 'framer-motion'
import { AvatarButtonGroup } from 'src/design-system'
import { CaretDown, CaretUp } from '@phosphor-icons/react'
import { observer } from 'mobx-react-lite'
import * as Accordion from '@radix-ui/react-accordion'
import * as React from 'react'
import { avatarGroupProps } from 'app/packs/src/utils/user-helpers'
import { Checkin } from 'store/modules/checkins'
import { CheckinSkill } from 'store/modules/checkin-skills'
import {
  CheckinStatus,
  CheckinStatusLabel,
} from 'components/checkin-status-label'
import { resolveRatingLabel } from '../../../utils/resolve-rating-label'
import { HtmlContent } from 'components/atoms/editor/html-content'
import { SensitiveContent } from 'components/sensitive-content'
import { User } from 'store/modules/users'
import { AttachmentDisplay } from 'components/attachments/display-attachment'
import { store } from 'store/index'
import cn from 'classnames'

export type CheckinSkillCardNotesProps = {
  checkin: Checkin
  checkinSkill: CheckinSkill
}

export const CheckinSkillCardNotes: React.FC<CheckinSkillCardNotesProps> =
  observer((props) => {
    const { checkin, checkinSkill } = props

    type AttachmentInfo = {
      id: string
      filename: string
      url?: string
      uploadedByUserName?: string
      uploadedByUserId?: string | number
      stage?: string
    }

    const attachmentData = ((
      checkinSkill as unknown as { attachmentData?: AttachmentInfo[] }
    ).attachmentData || []) as AttachmentInfo[]

    const authorAttachments = attachmentData.filter(
      (att: AttachmentInfo) => att.stage === 'author'
    )
    const assessorAttachments = attachmentData.filter(
      (att: AttachmentInfo) => att.stage === 'assessor'
    )
    const jointReviewAttachments = attachmentData.filter(
      (att: AttachmentInfo) => att.stage === 'joint_review'
    )

    const resolveForLevel = (level: number | null) => {
      if (level === null) {
        return {
          status: 'not_checked_in' as CheckinStatus,
          statusColor: null,
          statusLabel: null,
        }
      }

      const delta = level - checkinSkill.requiredLevel
      const status = checkin.labelSlugForDelta(delta)
      const resolved = resolveRatingLabel({
        status,
        ratingScaleLabels: checkin.ratingScaleLabels,
      })

      return {
        status,
        statusColor: resolved.color,
        statusLabel: resolved.text ?? null,
      }
    }

    const authorResolved = resolveForLevel(checkinSkill.level)
    const assessorResolved = resolveForLevel(checkinSkill.assessorLevel)

    return (
      <div className="flex flex-col gap-y-7 relative">
        {!checkin.jointReview && (
          <div className="absolute border-0 border-gray-100 border-l border-solid h-full ml-2.5 mt-1"></div>
        )}
        {!checkin.jointReview && (
          <>
            <CheckinSkillCardNote
              description={checkinSkill.description}
              skipped={checkinSkill.userSkipped}
              status={authorResolved.status}
              statusColor={authorResolved.statusColor}
              statusLabel={authorResolved.statusLabel}
              title={checkin.author.fullName}
              users={[checkin.author]}
              attachments={authorAttachments}
            />
            <CheckinSkillCardNote
              description={checkinSkill.assessorDescription}
              skipped={checkinSkill.assessorSkipped}
              status={assessorResolved.status}
              statusColor={assessorResolved.statusColor}
              statusLabel={assessorResolved.statusLabel}
              title={checkin.assessor?.fullName || 'Assessor'}
              users={checkin.assessor ? [checkin.assessor] : []}
              attachments={assessorAttachments}
            />
          </>
        )}
        <CheckinSkillCardNote
          description={checkinSkill.finalDescription}
          expandable={!checkin.jointReview}
          skipped={checkinSkill.finalSkipped}
          status={checkinSkill.status}
          statusLabel={checkinSkill.statusLabel}
          statusColor={checkinSkill.statusColor}
          title="Joint review"
          users={checkin.users}
          attachments={jointReviewAttachments}
        />
      </div>
    )
  })

export type CheckinSkillCardNoteProps = {
  description: string | null
  expandable?: boolean
  title: string
  skipped: boolean | null
  status: CheckinStatus
  statusColor?: string | null
  statusLabel?: string | null
  users: User[]
  attachments: Array<{
    id: string
    filename: string
    url?: string
    uploaded_by_user_name?: string
    uploaded_by_user_id?: string | number | null
  }>
}

export const CheckinSkillCardNote: React.FC<CheckinSkillCardNoteProps> =
  observer((props) => {
    const {
      description: defaultDescription,
      expandable = true,
      skipped,
      status,
      statusColor,
      statusLabel,
      title,
      users,
      attachments,
    } = props
    const currentUserId = store.currentUser?.id

    const hasRealText = (html: string | null | undefined): boolean => {
      if (!html) return false
      return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim().length > 0
    }

    const hasContent =
      skipped || hasRealText(defaultDescription) || (attachments && attachments.length > 0)

    const [open, setOpen] = React.useState(true)
    const onClick = React.useCallback(
      () => expandable && hasContent && setOpen(!open),
      [expandable, hasContent, open]
    )

    const Icon = open ? CaretUp : CaretDown

    let description = hasRealText(defaultDescription) ? defaultDescription : null
    if (!description) description = 'No answer'
    if (skipped) description = 'Skipped'

    return (
      <AnimatePresence>
        <Accordion.Root type="single">
          <Accordion.Item value="item-1">
            <Accordion.Header>
              {expandable && hasContent ? (
                <Accordion.Trigger asChild>
                  <div
                    className="flex items-center justify-between w-full text-sm font-normal appearance-none cursor-pointer"
                    onClick={onClick}
                  >
                    <div className="flex flex-row items-center gap-x-3">
                      <AvatarButtonGroup
                        avatarClassName="border-white border-px border-solid"
                        avatars={avatarGroupProps(users, { stateTooltip: true })}
                        size="xs"
                      />
                      <div>{title}</div>
                      <CheckinStatusLabel
                        status={status}
                        statusColor={statusColor}
                        statusLabel={statusLabel}
                      />
                    </div>
                    <Icon className="h-3.5 text-gray-200 w-3.5" weight="bold" />
                  </div>
                </Accordion.Trigger>
              ) : (
                <div className="flex items-center justify-between w-full text-sm font-normal">
                  <div className="flex flex-row items-center gap-x-3">
                    <AvatarButtonGroup
                      avatarClassName="border-white border-px border-solid"
                      avatars={avatarGroupProps(users, { stateTooltip: true })}
                      size="xs"
                    />
                    <div>{title}</div>
                    <CheckinStatusLabel
                      status={status}
                      statusColor={statusColor}
                      statusLabel={statusLabel}
                    />
                  </div>
                </div>
              )}
            </Accordion.Header>
            {open && hasContent && (
              <motion.div
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                initial={{ height: 0, opacity: 0 }}
                transition={{ ease: 'easeInOut', duration: 0.2 }}
              >
                <Accordion.Content className="ml-2.5" forceMount>
                  <SensitiveContent>
                    <div className="flex flex-col gap-y-3">
                      {(skipped || hasRealText(defaultDescription)) && (
                        <div className="flex flex-row gap-x-2">
                          <HtmlContent
                            className={cn(
                              'ml-4 mt-4',
                              (!hasRealText(defaultDescription) || skipped) && 'opacity-50'
                            )}
                          >
                            {description}
                          </HtmlContent>
                        </div>
                      )}
                      {attachments && attachments.length > 0 && (
                        <div className="ml-4 mt-2">
                          <AttachmentDisplay
                            attachments={attachments}
                            currentUserId={currentUserId}
                          />
                        </div>
                      )}
                    </div>
                  </SensitiveContent>
                </Accordion.Content>
              </motion.div>
            )}
          </Accordion.Item>
        </Accordion.Root>
      </AnimatePresence>
    )
  })
